import Highcharts from "highcharts";
import { enterpriseTokens, overviewChartPalette } from "./theme";

const chartColors = [
  overviewChartPalette.indigo,
  overviewChartPalette.teal,
  overviewChartPalette.cyan,
  overviewChartPalette.violet,
  overviewChartPalette.amber,
  overviewChartPalette.coral,
];

const semanticColor = (name, fallbackIndex) => {
  const label = String(name || "").toLowerCase();

  if (/closed|complete|completed|achieved|safe|resolved|approved|won|on track/.test(label)) {
    return overviewChartPalette.teal;
  }
  if (/open|overdue|delayed|critical|fatal|unsafe|rejected|lost|high risk|incident/.test(label)) {
    return overviewChartPalette.coral;
  }
  if (/pending|upcoming|due|warning|medium|in progress|active/.test(label)) {
    return overviewChartPalette.amber;
  }

  return chartColors[fallbackIndex % chartColors.length];
};

const installSeriesColorRemapping = () => {
  if (Highcharts.__infraTrackEnterpriseColorsInstalled) return;
  Highcharts.__infraTrackEnterpriseColorsInstalled = true;

  Highcharts.wrap(Highcharts.Chart.prototype, "init", function (proceed, userOptions, callback) {
    const options = userOptions || {};
    const chartType = options.chart?.type;
    const preservedTypes = new Set(["gauge", "solidgauge", "heatmap", "xrange", "gantt"]);
    const preserveColors = options.custom?.preserveSemanticColors || preservedTypes.has(chartType) || options.colorAxis;

    if (!preserveColors) {
      options.colors = chartColors;

      options.series?.forEach((series, seriesIndex) => {
        const seriesType = series.type || chartType;
        if (preservedTypes.has(seriesType) || series.custom?.preserveSemanticColors) return;

        series.color = semanticColor(series.name, seriesIndex);
        if (series.lineColor) series.lineColor = series.color;
        if (series.fillColor && typeof series.fillColor === "string") series.fillColor = series.color;
        if (series.colors) series.colors = chartColors;

        if (["pie", "variablepie", "funnel", "pyramid", "treemap"].includes(seriesType)) {
          series.data = series.data?.map((point, pointIndex) => {
            if (typeof point !== "object" || point === null || Array.isArray(point)) return point;
            return { ...point, color: semanticColor(point.name, pointIndex) };
          });
        }
      });
    }

    return proceed.call(this, options, callback);
  });
};

export const applyHighchartsTheme = () => {
  installSeriesColorRemapping();

  Highcharts.setOptions({
    colors: chartColors,
    chart: {
      backgroundColor: "transparent",
      borderWidth: 0,
      spacing: [14, 14, 12, 14],
      style: {
        fontFamily: '"Inter", "Outfit", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      },
    },
    title: {
      style: {
        color: enterpriseTokens.navy,
        fontSize: "14px",
        fontWeight: "700",
      },
    },
    subtitle: {
      style: {
        color: enterpriseTokens.muted,
        fontSize: "11px",
        fontWeight: "500",
      },
    },
    xAxis: {
      lineColor: enterpriseTokens.line,
      tickColor: enterpriseTokens.line,
      gridLineColor: overviewChartPalette.grid,
      labels: {
        style: { color: enterpriseTokens.slate, fontSize: "11px" },
      },
      title: {
        style: { color: enterpriseTokens.slate, fontSize: "11px", fontWeight: "600" },
      },
    },
    yAxis: {
      lineColor: enterpriseTokens.line,
      tickColor: enterpriseTokens.line,
      gridLineColor: overviewChartPalette.grid,
      gridLineDashStyle: "Dash",
      labels: {
        style: { color: enterpriseTokens.slate, fontSize: "11px" },
      },
      title: {
        style: { color: enterpriseTokens.slate, fontSize: "11px", fontWeight: "600" },
      },
    },
    legend: {
      symbolRadius: 1,
      symbolHeight: 9,
      symbolWidth: 9,
      itemDistance: 18,
      itemMarginTop: 3,
      itemMarginBottom: 3,
      itemStyle: {
        color: enterpriseTokens.slate,
        fontSize: "11px",
        fontWeight: "600",
      },
      itemHoverStyle: { color: enterpriseTokens.navy },
      itemHiddenStyle: { color: "#A8B3C4" },
    },
    tooltip: {
      backgroundColor: enterpriseTokens.navy,
      borderColor: enterpriseTokens.navy,
      borderRadius: 3,
      borderWidth: 0,
      shadow: false,
      padding: 10,
      style: { color: "#FFFFFF", fontSize: "12px" },
    },
    plotOptions: {
      series: {
        borderWidth: 0,
        animation: { duration: 350 },
        states: {
          hover: { brightness: -0.04, halo: { size: 4, opacity: 0.08 } },
          inactive: { opacity: 0.55 },
        },
        dataLabels: {
          style: {
            color: enterpriseTokens.navy,
            fontSize: "11px",
            fontWeight: "600",
            textOutline: "none",
          },
        },
      },
      column: { borderRadius: 1, groupPadding: 0.16, pointPadding: 0.08 },
      bar: { borderRadius: 1, groupPadding: 0.16, pointPadding: 0.08 },
      pie: { borderColor: "#FFFFFF", borderWidth: 3, slicedOffset: 4 },
      areaspline: { fillOpacity: 0.12, lineWidth: 2 },
      line: { lineWidth: 2, marker: { radius: 3 } },
      spline: { lineWidth: 2, marker: { radius: 3 } },
    },
    credits: { enabled: false },
    exporting: { enabled: false },
    lang: { thousandsSep: "," },
  });
};
